import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo } from "react";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import styles from "./style.module.scss";
import WatchlistCTAs from "./WatchlistCTAs";
import WatchlistsPageMobile from "./WatchlistsPageMobile";

const SignUpForm = dynamic(() => import("components/SignUpForm"));
const WatchlistsPageTabs = dynamic(() => import("./WatchlistsPageTabs"));
const WatchlistUpdates = dynamic(() => import("./WatchlistUpdates"));
const WatchlistsPageWidget = dynamic(() => import("./WatchlistsPageWidget"));

type WatchlistsPageProps = {};

const WatchlistsPage = ({}: WatchlistsPageProps) => {
  const media = useMediaQuery();

  const router = useRouter();

  const { authenticated } = useAuthentication();

  const { setShowBackButton } = useContext(TopNavBarContext);
  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);
  const title = "Watchlists | Smartkarma";
  const description =
    "Smartkarma is the Independent Investment Research Network, uniting Insight Providers, Investors, and Investor Relations Professionals.";

  const metaTags = useMemo(() => {
    const defaultMetatags = (
      <>
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://assets.smartkarma.com/v3/img/smartkarma-research-reinvented-dark.png"
        />
        <meta name="robots" content="index, follow" />
      </>
    );

    return (
      <>
        {defaultMetatags}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </>
    );
  }, [canonicalUrl]);

  const widgets = useMemo(
    () =>
      authenticated ? (
        <WatchlistsPageWidget />
      ) : (
        <CustomCard className="p-0">
          <SignUpForm type="compact" />
        </CustomCard>
      ),
    [authenticated]
  );

  if (media.mobile) {
    return (
      <>
        <Head>{metaTags}</Head>
        <WatchlistsPageMobile />
      </>
    );
  }

  return (
    <>
      <Head>{metaTags}</Head>
      <PaneLayout
        className={styles.layout}
        left={<WatchlistsPageTabs />}
        actionBar={
          <div className="pt-[75px] mx-[20px] pl-[10px] pr-[5px]">
            {authenticated && (
              <WatchlistCTAs
                watchlistId={router.query.watchlist as string}
                showBulkImport
                showUnfollowCTA
                onWatchlistRemoved={(nextWatchlist) =>
                  router.replace({
                    pathname: router.pathname,
                    query: {
                      ...router.query,
                      watchlist: nextWatchlist?.id,
                    },
                  })
                }
              />
            )}
          </div>
        }
        right={<div className="mt-[60px]">{widgets}</div>}
      >
        <div className={styles.content}>
          <WatchlistUpdates />
        </div>
      </PaneLayout>
    </>
  );
};

export default WatchlistsPage;
